import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import './index.scss';

/**
 * Mobile browsers render PDFs in iframes poorly (Android Chrome shows nothing,
 * iOS Safari only the first page), so those devices open the PDF in a new tab.
 */
export const shouldOpenPdfInNewTab = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(max-width: 768px), (pointer: coarse)').matches;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Full-screen overlay that displays a PDF via the browser's native viewer */
const PdfViewerModal = ({ open, url, title, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const closeBtnRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const titleId = useId();

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;

    setLoaded(false);
    restoreFocusRef.current = document.activeElement;
    closeBtnRef.current?.focus();

    const onKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // The opener may live in a menu that has since unmounted.
      if (restoreFocusRef.current?.isConnected) restoreFocusRef.current.focus();
    };
  }, [open, url]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="pdf-viewer"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pdf-viewer__panel" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="pdf-viewer__header">
          <h2 id={titleId} className="pdf-viewer__title">
            {title}
          </h2>
          <div className="pdf-viewer__actions">
            <a className="pdf-viewer__open" href={url} target="_blank" rel="noopener noreferrer">
              Open in new tab
            </a>
            <button
              ref={closeBtnRef}
              type="button"
              className="pdf-viewer__close"
              aria-label="Close"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="pdf-viewer__body">
          {!loaded ? <div className="pdf-viewer__loading" aria-hidden="true" /> : null}
          <iframe
            className="pdf-viewer__frame"
            src={url}
            title={title}
            onLoad={() => setLoaded(true)}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfViewerModal;
